import { inject, Injectable } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { UntilDestroy } from '@ngneat/until-destroy';
import { nonNullable } from '@rma/generic/rxjs/operators';
import { CurrentUserService } from '@rma/public/accounts/user';
import { DeviceIdentificationService } from '@rma/public/generic/util/device-identification';
import { Environment } from '@rma/public/generic/util/environment';
import { ChatContextService } from '@rma/public/generic/util/tracking/intercom';
import { combineLatest, filter } from 'rxjs';
import { HeapService } from './heap.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class HeapScriptService {
  private readonly chatApiService = inject(ChatContextService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly environment = inject(Environment);
  private readonly heapService = inject(HeapService);
  private readonly deviceIdService = inject(DeviceIdentificationService, {
    optional: true,
  });
  private readonly window = inject(WA_WINDOW);

  public initialiseHeap() {
    const environmentData = {
      rma_locale: this.environment.language.locale,
      rma_country_code: this.environment.countryCode,
      rma_domain: this.window.location.hostname,
    };

    this.heapService.addUserProperties(environmentData);

    combineLatest({
      user: this.currentUserService.currentUser$.pipe(
        nonNullable(),
        filter((c) => !!c.userId),
      ),
      userChatData: this.chatApiService.userChatData$,
    }).subscribe(({ user, userChatData }) => {
      // because we don't handle log-out, before we identify anybody, lets reset the ident
      // this makes sure we don't join two users together, but if we just re-ident as the same user anyway, things will be fine so 🧙‍♀️
      this.heapService.resetIdentity();

      if (!user.isImpersonating) {
        this.heapService.identify(user.userId);

        // https://developers.heap.io/docs/using-identify#when-and-where-to-call-identify-and-adduserproperties

        const { user_id, user_hash, app_id, rma_locale, ...userProperties } = userChatData;

        // If you ever get `TypeError: window.heap.push is not a function` ---> https://github.com/segmentio/analytics.js/issues/605
        this.heapService.addUserProperties(userProperties);

        if (this.deviceIdService) {
          this.heapService.addEventProperties({
            rma_device_id: this.deviceIdService.getDeviceId(),
          });
        }
      }

      this.heapService.addUserProperties({
        'rma.authenticatedViaRadmin': user.isImpersonating,
      });
    });
  }
}
